import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery, Dialog, Card, Chip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { styled, keyframes } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CameraFrontIcon from '@mui/icons-material/CameraFront';
import WatchIcon from '@mui/icons-material/Watch';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentOverlay from './AssessmentOverlay';
import SmartBiometricTherapyDialog from '../home/SmartBiometricTherapyDialog';
import { createSoundTherapyFeedback, getSoundTherapyFeedbackByUserId } from '../../service/echoSounds';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ManageYourStressImg from '../../assets/manageYourStressAndAnxiety.png';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { trackCTA } from '../../service/usercta';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import { getAudioCategories } from "../../service/audioCategory";

// Ambient orb animations
const orbFloat = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(20px, -15px) scale(1.03); }
  66% { transform: translate(-15px, 10px) scale(0.97); }
`;

const particleFloat = keyframes`
  0% { transform: translateY(0) scale(1); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(-220px) scale(0.3); opacity: 0; }
`;

const waveAnimation = keyframes`
  0%, 100% { height: 5px; opacity: 0.3; }
  50% { height: 20px; opacity: 1; }
`;

const sparkleAnimation = keyframes`
  0%, 100% { opacity: 0; transform: scale(0.5); }
  50% { opacity: 1; transform: scale(1.5); }
`;

const ringPulse = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  100% { transform: scale(1.6); opacity: 0; }
`;

const btnGlow = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const metaPulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  background: '#F2F6F5',
  overflow: 'hidden',
}));

const AmbientBackground = styled(Box)({
  position: 'fixed',
  inset: 0,
  zIndex: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.025'/%3E%3C/svg%3E")`,
    opacity: 0.6,
  },
});

const Orb = styled(Box)(({ top, right, bottom, left, size, color, delay }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: `radial-gradient(circle, ${color}, transparent 70%)`,
  filter: 'blur(110px)',
  animation: `${orbFloat} 18s ease-in-out infinite`,
  animationDelay: delay || '0s',
  ...(top && { top }),
  ...(right && { right }),
  ...(bottom && { bottom }),
  ...(left && { left }),
}));

// const ContentWrapper = styled(Box)({
//   position: 'relative',
//   zIndex: 1,
//   maxWidth: 1180,
//   margin: '0 auto',
//   padding: '0 24px 80px',
// });

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  maxWidth: 1180,
  margin: '0 auto',
  padding: '0 24px 80px',
  [theme.breakpoints.down('sm')]: {
    padding: '0 16px 60px',
  },
}));

// const HeroSection = styled(Box)(({ theme }) => ({
//   position: 'relative',
//   marginTop: 24,
//   borderRadius: 28,
//   overflow: 'hidden',
//   background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
//   padding: '40px 44px',
//   minHeight: 200,
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'space-between',
//   gap: 32,
//   boxShadow: '0 20px 50px rgba(20,42,48,0.09), 0 0 40px rgba(26,174,122,0.12)',
//   '&::before': {
//     content: '""',
//     position: 'absolute',
//     inset: 0,
//     background: 'radial-gradient(ellipse 300px 300px at 90% 80%, rgba(139,92,246,0.15), transparent), radial-gradient(ellipse 200px 200px at 10% 20%, rgba(245,158,11,0.1), transparent)',
//     pointerEvents: 'none',
//   },
//   [theme.breakpoints.down('md')]: {
//     flexDirection: 'column',
//     alignItems: 'flex-start',
//     padding: '28px 22px',
//     marginTop: 16,
//     minHeight: 'auto',
//     gap: 24,
//   },
// }));

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginTop: 24,
  borderRadius: 28,
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
  padding: '40px 44px',
  minHeight: 200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 32,
  boxShadow: '0 20px 50px rgba(20,42,48,0.09), 0 0 40px rgba(26,174,122,0.12)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(ellipse 300px 300px at 90% 80%, rgba(139,92,246,0.15), transparent), radial-gradient(ellipse 200px 200px at 10% 20%, rgba(245,158,11,0.1), transparent)',
    pointerEvents: 'none',
  },
  // laptop / smaller desktop
  [theme.breakpoints.between('md', 'lg')]: {
    padding: '32px 32px',
    gap: 24,
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '28px 22px',
    marginTop: 16,
    minHeight: 'auto',
    gap: 24,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '22px 16px',
    borderRadius: 20,
  },
}));

const HeroParticles = styled(Box)({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
  '& span': {
    position: 'absolute',
    width: 4,
    height: 4,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.15)',
    animation: `${particleFloat} linear infinite`,
  },
});

const HeroLeft = styled(Box)({
  position: 'relative',
  zIndex: 2,
  flex: 1,
});

const BackButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 18px',
  borderRadius: 40,
  background: 'transparent',
  color: '#1AAE7A',
  fontSize: 13,
  fontWeight: 600,
  border: '1.5px solid #1AAE7A',
  cursor: 'pointer',
  transition: 'all 0.3s ease',

  '& svg': {
    stroke: '#1AAE7A',
    transition: 'all 0.3s ease',
  },

  '&:hover': {
    background: '#1AAE7A',
    color: '#fff',
    boxShadow: '0 6px 18px rgba(26,174,122,0.3)',
  },

  '&:hover svg': {
    stroke: '#fff',
  },
}));

const HeroTitle = styled(Typography)({
  fontFamily: '"Inter", sans-serif',
  fontSize: 'clamp(28px, 4.5vw, 40px)',
  fontWeight: 600,
  color: '#fff',
  lineHeight: 1.15,
  letterSpacing: '-0.5px',
  marginBottom: 10,
});

const HeroSubtitle = styled(Typography)({
  fontSize: 15,
  color: 'rgba(255,255,255,0.7)',
  lineHeight: 1.6,
  maxWidth: 420,
  marginBottom: 20,
});

const TopRightBackButton = styled('button')(({ theme }) => ({
  position: 'absolute',
  top: 24,
  right: 32,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  borderRadius: 40,
  background: 'rgba(255,255,255,0.9)',
  color: '#1AAE7A',
  fontSize: 13,
  fontWeight: 600,
  border: '1px solid rgba(26,174,122,0.25)',
  cursor: 'pointer',
  backdropFilter: 'blur(8px)',
  transition: 'all 0.3s ease',
  zIndex: 20,

  '&:hover': {
    background: '#1AAE7A',
    color: '#fff',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(26,174,122,0.3)',
  },

  [theme.breakpoints.down('sm')]: {
    right: 16,
    top: 16,
  },
}));

const WaveIndicator = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  padding: '8px 18px',
  background: 'rgba(255,255,255,0.08)',
  borderRadius: 50,
  border: '1px solid rgba(255,255,255,0.08)',
  width: 'fit-content',
  '& .wave-bar': {
    width: 3,
    borderRadius: 2,
    background: 'rgba(255,255,255,0.7)',
    animation: `${waveAnimation} 1.1s ease-in-out infinite`,
  },
  '& .wave-label': {
    fontSize: 11,
    color: 'rgba(255,255,255,0.6)',
    fontWeight: 600,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    marginLeft: 10,
  },
});

// const HeroRight = styled(Box)(({ theme }) => ({
//   position: 'relative',
//   zIndex: 2,
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'center',
//   flexShrink: 0,
//   [theme.breakpoints.down('md')]: {
//     alignSelf: 'stretch',
//   },
// }));

const HeroRight = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  [theme.breakpoints.up('lg')]: {
    maxWidth: 420,
  },
  [theme.breakpoints.between('md', 'lg')]: {
    maxWidth: 360,
  },
  [theme.breakpoints.down('md')]: {
    alignSelf: 'stretch',
  },
}));

const AssessmentCard = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(160deg, rgba(255,255,255,0.13) 0%, rgba(255,255,255,0.04) 100%)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: 22,
  padding: '32px 28px 26px',
  textAlign: 'center',
  width: 260,
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: -1,
    borderRadius: 23,
    padding: 1,
    background: 'linear-gradient(160deg, rgba(255,255,255,0.25), rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.1))',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    pointerEvents: 'none',
  },
  '&:hover': {
    transform: 'translateY(-6px)',
    background: 'linear-gradient(160deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 100%)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 0 40px rgba(26,174,122,0.08)',
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    padding: '28px 22px 24px',
  },
}));

const FeaturedTop = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '12px 16px 0 16px',
});

const FeaturedChip = styled('div')({
  position: 'absolute',
  top: 25,
  left: '49%',
  transform: 'translate(-50%, -50%)',
  padding: '5px 14px',
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  background: '#fff',
  color: '#117D58',
  boxShadow: '0 6px 16px rgba(26,174,122,0.35)',
  zIndex: 2,
});

const AssessmentParticles = styled(Box)({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
  '& span': {
    position: 'absolute',
    width: 3,
    height: 3,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.3)',
    animation: `${sparkleAnimation} 4s ease-in-out infinite`,
    '&:nth-of-type(1)': { top: '15%', left: '12%', animationDelay: '0s', width: 2, height: 2 },
    '&:nth-of-type(2)': { top: '25%', right: '15%', animationDelay: '1s' },
    '&:nth-of-type(3)': { bottom: '30%', left: '18%', animationDelay: '2s', width: 2, height: 2 },
    '&:nth-of-type(4)': { bottom: '20%', right: '20%', animationDelay: '0.5s' },
    '&:nth-of-type(5)': { top: '50%', left: '50%', animationDelay: '3s', width: 2, height: 2 },
  },
});

const RingVisual = styled(Box)({
  width: 68,
  height: 68,
  margin: '0 auto 18px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const RingPulse = styled(Box)(({ delay }) => ({
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  border: '1.5px solid rgba(255,255,255,0.2)',
  animation: `${ringPulse} 2.5s ease-out infinite`,
  animationDelay: delay || '0s',
}));

const IconInner = styled(Box)({
  width: 52,
  height: 52,
  borderRadius: 15,
  background: 'rgba(255,255,255,0.12)',
  border: '1px solid rgba(255,255,255,0.15)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  zIndex: 2,
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
});

const AssessmentTitle = styled(Typography)({
  fontFamily: '"Inter", sans-serif',
  fontSize: 19,
  fontWeight: 600,
  color: '#fff',
  lineHeight: 1.3,
  marginBottom: 10,
  letterSpacing: '-0.2px',
});

const AssessmentDesc = styled(Typography)({
  fontSize: 12.5,
  color: 'rgba(255,255,255,0.55)',
  lineHeight: 1.55,
  marginBottom: 20,
});

const GlowButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 28px',
  borderRadius: 50,
  background: '#fff',
  color: '#117D58',
  fontFamily: '"Inter", sans-serif',
  fontSize: 13.5,
  fontWeight: 700,
  textDecoration: 'none',
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.35s',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 4px 16px rgba(0,0,0,0.1), 0 0 20px rgba(255,255,255,0.08)',
  '&:hover': {
    transform: 'scale(1.06)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.15), 0 0 30px rgba(255,255,255,0.12)',
  },
});

const ButtonGlow = styled(Box)({
  position: 'absolute',
  inset: 0,
  borderRadius: 50,
  background: 'linear-gradient(90deg, transparent, rgba(26,174,122,0.12), transparent)',
  animation: `${btnGlow} 3s ease-in-out infinite`,
});

const MetaInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  marginTop: 16,
  fontSize: 11,
  color: 'rgba(255,255,255,0.35)',
  fontWeight: 500,
  letterSpacing: 0.3,
});

const MetaDot = styled(Box)({
  width: 5,
  height: 5,
  borderRadius: '50%',
  background: 'rgba(26,174,122,0.6)',
  animation: `${metaPulse} 2s ease-in-out infinite`,
});

// const SectionHead = styled(Box)({
//   display: 'flex',
//   alignItems: 'center',
//   gap: 14,
//   margin: '36px 0 22px',
// });

const SectionHead = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  margin: '36px 0 22px',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    margin: '28px 0 16px',
    gap: 10,
  },
}));

const SectionIcon = styled(Box)(({ color }) => ({
  width: 36,
  height: 36,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  background: color === 'green' ? 'rgba(26,174,122,0.1)' : 'rgba(13,148,136,0.1)',
  color: color === 'green' ? '#1AAE7A' : '#0D9488',
}));

const SectionTitle = styled(Typography)({
  fontFamily: '"Inter", sans-serif',
  fontSize: 20,
  fontWeight: 600,
  color: '#142A30',
});

const SectionLine = styled(Box)({
  flex: 1,
  height: 1,
  background: 'rgba(20,42,48,0.06)',
});

const SectionCount = styled(Box)({
  fontSize: 11,
  fontWeight: 600,
  color: '#8DABB4',
  background: '#F2F6F5',
  border: '1px solid rgba(20,42,48,0.06)',
  padding: '4px 12px',
  borderRadius: 50,
  letterSpacing: 0.3,
});

// const CardsGrid = styled(Box)(({ theme }) => ({
//   display: 'grid',
//   gridTemplateColumns: 'repeat(2, 1fr)',
//   gap: 20,
//   maxWidth: '100%',
//   width: '100%',
//   [theme.breakpoints.up('md')]: {
//     gap: 24,
//   },
//   [theme.breakpoints.up('lg')]: {
//     maxWidth: 1200,
//     margin: '0 auto',
//     gap: 28,
//   },
//   [theme.breakpoints.down('sm')]: {
//     gridTemplateColumns: '1fr',
//     gap: 16,
//   },
// }));


const CardsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 20,
  maxWidth: '100%',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    gap: 24,
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: 1200,
    margin: '0 auto',
    gap: 28,
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: 16,
  },
}));

const TherapyCard = styled(Box)(({ gradient }) => ({
  position: 'relative',
  borderRadius: 20,
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
  boxShadow: '0 4px 20px rgba(20,42,48,0.08)',
  background: '#FFFFFF',
  opacity: 0,
  transform: 'translateY(24px)',
  animation: `${slideUp} 0.6s ease-out forwards`,
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 24px 60px rgba(20,42,48,0.12)',
    '& img': {
      transform: 'scale(1.08)',
    },
  },
}));

// const CardInner = styled(Box)({
//   display: 'flex',
//   height: 240,
//   overflow: 'hidden',
// });

const CardInner = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: 240,
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    height: 220,
  },
  [theme.breakpoints.down('sm')]: {
    height: 190,
  },
}))

// const CardImage = styled(Box)({
//   width: '50%',
//   flexShrink: 0,
//   overflow: 'hidden',
//   position: 'relative',
//   '& img': {
//     width: '100%',
//     height: '100%',
//     objectFit: 'cover',
//     transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
//   },
// });


const CardImage = styled(Box)(({ theme }) => ({
  width: '50%',
  flexShrink: 0,
  overflow: 'hidden',
  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '42%',
  },
}));

// const CardBody = styled(Box)(({ gradient }) => ({
//   flex: 1,
//   display: 'flex',
//   flexDirection: 'column',
//   alignItems: 'center',
//   justifyContent: 'center',
//   padding: '24px 20px',
//   textAlign: 'center',
//   position: 'relative',
//   background: gradient,
// }));

// const CardTitle = styled(Typography)({
//   fontFamily: '"Inter", sans-serif',
//   fontSize: 19,
//   fontWeight: 600,
//   color: '#fff',
//   lineHeight: 1.25,
//   marginBottom: 18,
// });

const CardBody = styled(Box)(({ theme, gradient }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px 20px',
  textAlign: 'center',
  position: 'relative',
  background: gradient,
  [theme.breakpoints.down('sm')]: {
    padding: '14px 10px',
  },
}));


const CardTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
  fontSize: 'clamp(15px, 3vw, 19px)',
  fontWeight: 600,
  color: '#fff',
  lineHeight: 1.25,
  marginBottom: 18,
  [theme.breakpoints.down('sm')]: {
    marginBottom: 12,
  },
}));

// const CardButton = styled('div')(({ theme }) => ({
//   display: 'inline-flex',
//   alignItems: 'center',
//   gap: 8,
//   padding: '10px 20px',
//   borderRadius: 50,
//   fontSize: 12,
//   fontWeight: 600,
//   border: '1px solid rgba(255,255,255,0.18)',
//   background: 'rgba(255,255,255,0.16)',
//   backdropFilter: 'blur(8px)',
//   color: '#fff',
//   letterSpacing: 0.2,

//   '& svg': {
//     flexShrink: 0
//   },

//   [theme.breakpoints.down('sm')]: {
//     borderRadius: 30,
//   }
// }));

const CardButton = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 20px',
  borderRadius: 50,
  fontSize: 12,
  fontWeight: 600,
  border: '1px solid rgba(255,255,255,0.18)',
  background: 'rgba(255,255,255,0.16)',
  backdropFilter: 'blur(8px)',
  color: '#fff',
  letterSpacing: 0.2,
  lineHeight: 1.45,
  maxWidth: 220,
  width: 'fit-content',

  '& svg': {
    flexShrink: 0,
    marginTop: 0,
  },

  '& .card-btn-label': {
    flex: 1,
    textAlign: 'left',
  },

  [theme.breakpoints.down('sm')]: {
    padding: '9px 16px',
    fontSize: 11,
    maxWidth: 180,
    borderRadius: 30,
  },
}));

const FeaturedRow = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 20,
  marginBottom: 20,
});

// const FeaturedCard = styled(Box)(({ theme }) => ({
//   position: 'relative',
//   borderRadius: 24,
//   overflow: 'hidden',
//   boxShadow: '0 8px 24px rgba(20,42,48,0.07)',
//   maxWidth: 520,
//   width: '100%',
//   opacity: 0,
//   transform: 'translateY(24px)',
//   animation: `${slideUp} 0.6s ease-out 0.2s forwards`,
//   [theme.breakpoints.down('sm')]: {
//     maxWidth: '100%',
//   },
// }));
const FeaturedCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: 24,
  overflow: 'hidden',
  boxShadow: '0 8px 24px rgba(20,42,48,0.07)',
  maxWidth: 520,
  width: '100%',
  opacity: 0,
  transform: 'translateY(24px)',
  animation: `${slideUp} 0.6s ease-out 0.2s forwards`,
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// const FeaturedInner = styled(Box)({
//   display: 'flex',
//   height: 210,
//   overflow: 'hidden',
// });
const FeaturedInner = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: 210,
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    minHeight: 190,
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: 170,
  },
}));

// const FeaturedImage = styled(Box)({
//   width: '48%',
//   flexShrink: 0,
//   overflow: 'hidden',
//   '& img': {
//     width: '100%',
//     height: '100%',
//     objectFit: 'cover',
//     transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
//   },
// });
const FeaturedImage = styled(Box)(({ theme }) => ({
  width: '48%',
  flexShrink: 0,
  overflow: 'hidden',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '42%',
  },
}));

// const FeaturedBody = styled(Box)({
//   flex: 1,
//   display: 'flex',
//   flexDirection: 'column',
//   alignItems: 'center',
//   justifyContent: 'center',
//   padding: '24px 22px',
//   textAlign: 'center',
//   background: 'linear-gradient(135deg, #0FA89E, #3DD9C8)',
//   position: 'relative',
// });

// Feedback Section Styles

const FeaturedBody = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px 22px',
  textAlign: 'center',
  background: 'linear-gradient(135deg, #0FA89E, #3DD9C8)',
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    padding: '16px 12px',
  },
}));

const FeedbackCard = styled(Box)(({ theme }) => ({
  background: '#FFFFFF',
  border: '1px solid rgba(20,42,48,0.06)',
  borderRadius: 24,
  padding: '28px 30px',
  boxShadow: '0 2px 8px rgba(20,42,48,0.05)',
  transition: 'all 0.35s',
  marginTop: 20,
  opacity: 0,
  transform: 'translateY(24px)',
  animation: `${slideUp} 0.6s ease-out 0.3s forwards`,
  '&:hover': {
    boxShadow: '0 8px 24px rgba(20,42,48,0.07)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '22px 18px',
  },
}));

const FeedbackPrompt = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  fontSize: 14,
  color: '#4A6E78',
  lineHeight: 1.6,
  marginBottom: 20,
  '& svg': {
    flexShrink: 0,
    marginTop: 2,
  },
});

const FeedbackMoodRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  marginBottom: 18,
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    gap: 12,
  },
}));

const FeedbackMoodLabel = styled(Typography)({
  fontSize: 13,
  fontWeight: 600,
  color: '#142A30',
  whiteSpace: 'nowrap',
});

const MoodButtons = styled(Box)({
  display: 'flex',
  gap: 6,
});

const MoodButton = styled('button')(({ selected }) => ({
  width: 44,
  height: 44,
  borderRadius: '50%',
  background: selected ? 'rgba(26,174,122,0.1)' : '#F2F6F5',
  border: `2px solid ${selected ? '#1AAE7A' : 'rgba(20,42,48,0.06)'}`,
  fontSize: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.25s',
  lineHeight: 1,
  padding: 0,
  boxShadow: selected ? '0 0 0 3px rgba(26,174,122,0.12)' : 'none',
  '&:hover': {
    borderColor: '#1AAE7A',
    transform: 'scale(1.15)',
    background: 'rgba(26,174,122,0.1)',
  },
}));

const FeedbackInput = styled('textarea')({
  width: '100%',
  border: '1px solid rgba(20,42,48,0.06)',
  borderRadius: 14,
  padding: '14px 16px',
  fontFamily: '"Inter", sans-serif',
  fontSize: 14,
  color: '#142A30',
  background: '#F2F6F5',
  resize: 'none',
  transition: 'border-color 0.25s, box-shadow 0.25s',
  outline: 'none',
  marginBottom: 16,
  '&::placeholder': {
    color: '#8DABB4',
  },
  '&:focus': {
    borderColor: '#1AAE7A',
    boxShadow: '0 0 0 3px rgba(26,174,122,0.12)',
  },
});

const FeedbackActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

const FeedbackTags = styled(Box)({
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
});

const TagButton = styled('button')(({ selected }) => ({
  padding: '7px 16px',
  borderRadius: 50,
  background: selected ? 'rgba(26,174,122,0.1)' : '#F2F6F5',
  border: `1px solid ${selected ? '#1AAE7A' : 'rgba(20,42,48,0.06)'}`,
  fontFamily: '"Inter", sans-serif',
  fontSize: 12.5,
  fontWeight: 500,
  color: selected ? '#117D58' : '#4A6E78',
  cursor: 'pointer',
  transition: 'all 0.25s',
  '&:hover': {
    borderColor: '#1AAE7A',
    background: 'rgba(26,174,122,0.1)',
    color: '#117D58',
  },
}));

const SubmitButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 26px',
  borderRadius: 60,
  background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
  color: '#fff',
  fontFamily: '"Inter", sans-serif',
  fontSize: 13.5,
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.3s',
  boxShadow: '0 4px 14px rgba(26,174,122,0.28)',
  whiteSpace: 'nowrap',
  flexShrink: 0,

  '&:hover': {
    transform: 'translateY(-2px) scale(1.03)',
    boxShadow: '0 6px 20px rgba(26,174,122,0.38)',
  },

  '&:disabled': {
    background: '#cfd8dc',
    cursor: 'not-allowed',
    boxShadow: 'none',
    transform: 'none',
  },

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'center',
  },
}));

// Testimonial Styles
const TestimonialCard = styled(Box)(({ theme }) => ({
  background: '#FFFFFF',
  border: '1px solid rgba(20,42,48,0.06)',
  borderRadius: 24,
  padding: '28px 32px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 20,
  boxShadow: '0 2px 8px rgba(20,42,48,0.05)',
  transition: 'all 0.35s',
  position: 'relative',
  overflow: 'hidden',
  marginTop: 20,
  opacity: 0,
  transform: 'translateY(24px)',
  animation: `${slideUp} 0.6s ease-out 0.4s forwards`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: 4,
    height: '100%',
    background: 'linear-gradient(to bottom, #1AAE7A, #0D9488)',
    borderRadius: '0 4px 4px 0',
  },
  '&:hover': {
    boxShadow: '0 8px 24px rgba(20,42,48,0.07)',
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '22px 20px',
    gap: 14,
    flexDirection: 'column',
  },
}));

const TestimonialAvatar = styled(Box)({
  width: 48,
  height: 48,
  borderRadius: 14,
  background: 'linear-gradient(135deg, #a8edea, #fed6e3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 22,
  flexShrink: 0,
});

const TestimonialBody = styled(Box)({
  flex: 1,
});

const TestimonialQuote = styled(Typography)({
  fontSize: 15,
  color: '#4A6E78',
  lineHeight: 1.65,
  fontStyle: 'italic',
  marginBottom: 10,
});

const TestimonialMeta = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 6,
  },
}));

const TestimonialName = styled(Typography)({
  fontSize: 13,
  fontWeight: 600,
  color: '#142A30',
});

const TestimonialMetaDot = styled(Box)(({ theme }) => ({
  width: 3,
  height: 3,
  borderRadius: '50%',
  background: '#8DABB4',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const TestimonialDetail = styled(Typography)({
  fontSize: 12,
  color: '#8DABB4',
  fontWeight: 500,
});

const TestimonialStars = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 2,
  marginLeft: 'auto',
  color: '#F59E0B',
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    marginTop: 6,
  },
}));

const settings = {
  dots: false,
  infinite: true,
  speed: 800,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 10000,
  arrows: false,
  pauseOnHover: true,
};

const PageWrapper = styled('div')({
  width: '100%',
});

const TopBar = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '20px 40px',
});

// const SubmitButton = styled.button`
//   opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
//   cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
//   transition: 0.3s;
// `;

const EchoSounds = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const storedUser = JSON.parse(localStorage.getItem("user") || "null");
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user"));
  const accountId = user?.account_id;
  const [categories, setCategories] = useState([]);
  // Assessment dialog state
  const [openSmartDialog, setOpenSmartDialog] = useState(false);
  // Method selection and biometric dialog state
  const [openMethodDialog, setOpenMethodDialog] = useState(false);
  const [openBiometricDialog, setOpenBiometricDialog] = useState(false);
  const [biometricMode, setBiometricMode] = useState('manual');

  // Therapy cards section state
  const [showTherapyCards, setShowTherapyCards] = useState(false);
  // Feedback state
  const [selectedMood, setSelectedMood] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [feedbackText, setFeedbackText] = useState('');
  const [smartDialogMode, setSmartDialogMode] = useState('manual');
  const moods = ['😞', '😕', '😐', '😊', '🤩'];
  const tags = ['Relaxed', 'Focused', 'Calm', 'Energised', 'Sleepy'];
  const [feedbacks, setFeedbacks] = useState([]);
  const userId = storedUser.id; // change dynamically if needed

  const categoryGradients = {
    rejuvenate_echo_sounds: 'linear-gradient(135deg, #9B6A3E, #C89250)',
    echo_soundscapes: 'linear-gradient(135deg, #7231BD, #A855F7)',
    manage_your_thoughts: 'linear-gradient(135deg, #D97A1E, #F5A623)',
    sleep_stories: 'linear-gradient(135deg, #1E3A8A, #3B82F6)',
  };

  const categoryRoutes = {
    rejuvenate_echo_sounds: '/rejuvenate-sounds',
    echo_soundscapes: '/echo-soundscapes',
    manage_your_thoughts: '/sound-meditation',
    sleep_stories: '/sleep-stories',
  };


  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        console.log("calling category API");

        const response = await getAudioCategories();

        console.log("API response:", response);

        setCategories(response.data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);


  useEffect(() => {
    fetchFeedbacks();
  }, []);


  const fetchFeedbacks = async () => {
    try {
      const response = await getSoundTherapyFeedbackByUserId(accountId);
      setFeedbacks(response.feedbacks || []);
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
    }
  };



  const handleTagToggle = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleSubmitFeedback = async () => {
    try {
      if (selectedMood === null) {
        alert("Please rate your experience");
        return;
      }

      const payload = {
        user_id: userId, // 🔥 Replace with logged-in user ID
        account_id: accountId,
        experience: selectedMood + 1, // if moods are 0–4 index based
        comment: feedbackText,
        current_emmotion: selectedTags.join(","), // convert array to comma string
      };

      await createSoundTherapyFeedback(payload);

      alert("Feedback submitted successfully!");

      // Reset form
      setSelectedMood(null);
      setSelectedTags([]);
      setFeedbackText("");
      fetchFeedbacks();

    } catch (error) {
      console.error("Submit error:", error);
      alert(error?.error || "Failed to submit feedback");
    }
  };



  // Generate particles for hero
  const heroParticles = Array.from({ length: 20 }, (_, i) => ({
    left: `${Math.random() * 100}%`,
    duration: `${4 + Math.random() * 6}s`,
    delay: `${Math.random() * 8}s`,
    size: `${2 + Math.random() * 4}px`,
  }));

  // Generate wave bars
  const waveBars = Array.from({ length: 16 }, (_, i) => ({
    delay: `${i * 0.08}s`,
  }));

  const isFormValid =
    selectedMood !== null &&
    selectedTags.length > 0 &&
    feedbackText.trim() !== "";

  return (
    <>
      {showTherapyCards ? (
        // Therapy Cards Page
        <PageContainer>
          <AmbientBackground>
            <Orb top="10%" right="15%" size="320px" color="rgba(26,174,122,0.15)" delay="0s" />
            <Orb bottom="20%" left="10%" size="280px" color="rgba(13,148,136,0.12)" delay="3s" />
            <Orb top="60%" right="8%" size="240px" color="rgba(139,92,246,0.08)" delay="6s" />
          </AmbientBackground>

          <ContentWrapper>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px',
              marginTop: '40px'
            }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: '#22c55e',
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}
              >
                echo Sounds
              </Typography>
              <IconButton
                onClick={() => setShowTherapyCards(false)}
                sx={{
                  background: '#F0F5FF',
                  width: { xs: 32, sm: 36, md: 42 },
                  height: { xs: 32, sm: 36, md: 42 },
                  borderRadius: '50%',
                  color: '#0B2D6B',
                  boxShadow: '0 6px 16px rgba(26,174,122,0.3)',
                  transition: 'all 0.3s ease',

                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 22px rgba(26,174,122,0.4)',
                    background: '#F0F5FF',
                  },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
              </IconButton>
            </Box>

            <SectionHead>
              <SectionIcon color="green">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5Z" />
                  <path d="M12 5L8 21l4-7 4 7-4-16Z" />
                </svg>
              </SectionIcon>
              <SectionTitle>Explore Therapies for Quick Reset</SectionTitle>
              <SectionLine />
              <SectionCount>4 Experiences</SectionCount>
            </SectionHead>

            <FeaturedRow>
              <FeaturedCard>
                <FeaturedInner>
                  <FeaturedImage>
                    <FeaturedChip
                      sx={{
                        position: "absolute",
                        top: 12,
                        left: 43,
                        display: { xs: "block", sm: "none" }
                      }}
                    >
                      Featured
                    </FeaturedChip>
                    <img src={ManageYourStressImg} alt="Manage Anxiety" loading="lazy" />
                  </FeaturedImage>

                  <FeaturedBody>
                    <FeaturedChip sx={{ display: { xs: "none", sm: "block" }, }}>Featured</FeaturedChip>

                    <CardTitle>
                      Manage Your<br />Anxiety & Stress
                    </CardTitle>

                    <CardButton>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4" />
                        <path d="M12 8h.01" />
                      </svg>
                      Personalised experience based on your Stress levels
                    </CardButton>
                  </FeaturedBody>
                </FeaturedInner>
              </FeaturedCard>
            </FeaturedRow>

            <CardsGrid>
              {categories.map((category, index) => (
                <TherapyCard key={index} onClick={() => {
                  const user = JSON.parse(localStorage.getItem('user') || '{}');
                  trackCTA('sound_therapy', user);
                  navigate(categoryRoutes[category.name] || '/care');
                }} style={{ animationDelay: `${index * 0.07}s` }}>
                  <CardInner>
                    <CardImage>
                      <img src={category.image_url} alt={category.display_name} loading="lazy" />
                    </CardImage>
                    <CardBody gradient={categoryGradients[category.name] || "linear-gradient(135deg, #0FA89E, #3DD9C8)"}>
                      <CardTitle>{category.display_name}</CardTitle>
                      <CardButton>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                          <path d="M9 18V5l12-2v13" />
                          <circle cx="6" cy="18" r="3" />
                          <circle cx="18" cy="16" r="3" />
                        </svg>
                        {category.label}
                      </CardButton>
                    </CardBody>
                  </CardInner>
                </TherapyCard>
              ))}
            </CardsGrid>
          </ContentWrapper>
        </PageContainer>
      ) : (
        // Original Main Page
        <>
          <AssessmentOverlay
            open={openSmartDialog}
            onClose={() => setOpenSmartDialog(false)}
          />

          <SmartBiometricTherapyDialog
            open={openBiometricDialog}
            onClose={() => setOpenBiometricDialog(false)}
            mode={biometricMode}
          />

          {/* Method Selection Dialog */}

          {/* <Dialog
            open={openMethodDialog}
            onClose={() => setOpenMethodDialog(false)}
            PaperProps={{
              sx: {
                borderRadius: '22px',
                maxWidth: 420,
                width: '100%',
                p: 0,
                overflow: 'visible',
                maxHeight: '85vh',
                background: '#f5f5fa', // matches page bg so the brand strip blends in
                boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>

              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.25,
                px: 2.5, py: 2, flexShrink: 0, position: 'relative',
              }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '12px',
                  background: 'linear-gradient(135deg, #0d9488, #7c3aed)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20, flexShrink: 0,
                }}>
                  🌟
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.1, color: '#18162a' }}>
                    echoShine
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#5e5c72', lineHeight: 1.3 }}>
                    Student Wellness Assessment
                  </Typography>
                </Box>

                <IconButton
                  onClick={() => setOpenMethodDialog(false)}
                  sx={{ position: 'absolute', top: 8, right: 8, color: '#908ea3' }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Box sx={{
                background: '#fff',
                borderRadius: '22px',
                border: '1px solid #dddde6',
                boxShadow: '0 2px 12px rgba(0,0,0,.04)',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
                flex: 1,
                minHeight: 0,
              }}>
              
                <Box
                  sx={{
                    px: 3,
                    pt: 2.5,
                    pb: 1.5,
                    textAlign: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px', mx: 'auto', mb: 2.25,
                    background: 'linear-gradient(135deg, #0d9488, #7c3aed)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 32,
                    boxShadow: '0 8px 24px rgba(13,148,136,0.22), 0 8px 24px rgba(124,58,237,0.18)',
                  }}>
                    🌟
                  </Box>

                  <Typography sx={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontSize: 24,
                    fontWeight: 800,
                    color: '#18162a',
                    mb: 1.25,
                    lineHeight: 1.2,
                  }}>
                    Manage Your Anxiety &amp; Stress
                  </Typography>
                  <Typography sx={{
                    fontSize: 13.5,
                    color: '#5e5c72',
                    lineHeight: 1.6,
                    maxWidth: 300,
                    mx: 'auto',
                  }}>
                    Choose how you'd like to check in today. Select a method that works best for you.
                  </Typography>
                </Box>

                <Box sx={{
                  px: 2.5, pb: 3, pt: 0.75,
                  display: 'flex', flexDirection: 'column', gap: 1.5,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-track': { background: 'transparent' },
                  '&::-webkit-scrollbar-thumb': { background: '#dddde6', borderRadius: 10 },
                  '&::-webkit-scrollbar-thumb:hover': { background: '#cbcbd8' },
                }}>
                  {[
                    { icon: <SelfImprovementIcon sx={{ fontSize: 22, color: '#0d9488' }} />, label: 'Decode Your Stress — Let Echo Sounds Reset You', subtitle: 'Body scan + energy check with personalised sound therapy', iconBg: 'linear-gradient(135deg, rgba(13,148,136,.12), rgba(124,58,237,.08))', disabled: false, mode: 'decode' },
                    { icon: <CameraFrontIcon sx={{ fontSize: 22, color: '#7c3aed' }} />, label: 'Face Recognition', subtitle: 'Analyze your expressions via camera', iconBg: 'rgba(139,92,246,.08)', disabled: true, mode: 'face' },
                    { icon: <WatchIcon sx={{ fontSize: 22, color: '#ec4899' }} />, label: 'Biometric', subtitle: 'Use sensors to measure your vitals', iconBg: 'rgba(236,72,153,.08)', disabled: true, mode: 'biometric' },
                    // { icon: <AssignmentIcon sx={{ fontSize: 22, color: '#0d9488' }} />, label: 'Manual', subtitle: 'Self-report your current state', iconBg: 'rgba(13,148,136,.1)', disabled: false, mode: 'manual' },
                  ].map((method, idx) => (
                    <Card
                      key={idx}
                      elevation={0}
                      onClick={() => {
                        if (method.disabled) return;
                        setOpenMethodDialog(false);
                        if (method.mode === 'decode') {
                          navigate("/decode-stress");
                        } else {
                          setBiometricMode(method.mode || 'manual');
                          setOpenBiometricDialog(true);
                        }
                      }}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.75, p: '18px 16px',
                        borderRadius: '16px',
                        border: '1.5px solid #dddde6',
                        boxShadow: 'none',
                        opacity: method.disabled ? 0.55 : 1,
                        transition: 'all 0.3s',
                        cursor: method.disabled ? 'default' : 'pointer',
                        pointerEvents: method.disabled ? 'none' : 'auto',
                        flexShrink: 0,
                        '&:hover': method.disabled ? {} : {
                          borderColor: '#0d9488',
                          background: 'rgba(13,148,136,.07)',
                          boxShadow: '0 4px 20px rgba(13,148,136,.22)',
                          transform: 'translateY(-2px)',
                        },
                        '&:active': method.disabled ? {} : { transform: 'scale(0.98)' },
                      }}
                    >
                      <Box sx={{
                        width: 48, height: 48, borderRadius: '14px',
                        background: method.iconBg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {method.icon}
                      </Box>
                      <Box sx={{ textAlign: 'left', flex: 1 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#18162a', mb: '2px' }}>
                          {method.label}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#5e5c72', lineHeight: 1.4 }}>
                          {method.subtitle}
                        </Typography>
                      </Box>
                      {method.disabled ? (
                        <Chip
                          label="Coming Soon"
                          size="small"
                          sx={{
                            fontSize: 10, fontWeight: 700, height: 22, px: '2px',
                            background: 'rgba(13,148,136,.07)',
                            color: '#0d9488', border: '1px solid rgba(13,148,136,.15)',
                            letterSpacing: '.3px',
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <ChevronRightIcon sx={{ color: '#908ea3', flexShrink: 0 }} />
                      )}
                    </Card>
                  ))}
                </Box>
              </Box>
            </Box>
          </Dialog> */}
          {/* Method Selection Dialog */}
          <Dialog
            open={openMethodDialog}
            onClose={() => setOpenMethodDialog(false)}
            PaperProps={{
              sx: {
                borderRadius: 4,
                maxWidth: 420,
                width: '100%',
                p: 0,
                overflow: 'visible',
              }
            }}
          >
            <Box sx={{ position: 'relative', textAlign: 'center', pt: 5, pb: 3, px: 3 }}>
              {/* Floating heart icon */}
              <Box sx={{
                position: 'absolute', top: -28, left: '50%', transform: 'translateX(-50%)',
                width: 56, height: 56, borderRadius: '16px',
                // background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                background: 'linear-gradient(135deg, #0d9488, #7c3aed)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(34,197,94,0.3)',
              }}>
                <span style={{ fontSize: 28 }}>🌟</span>
              </Box>

              {/* Close button */}
              <IconButton
                onClick={() => setOpenMethodDialog(false)}
                sx={{ position: 'absolute', top: 8, right: 8, color: '#999' }}
              >
                <CloseIcon />
              </IconButton>

              <Typography variant="h6" fontWeight={700} sx={{ mt: 1, mb: 0.5 }}>
                Manage Your Anxiety & Stress
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Choose how you'd like to check in today. Select a method that works best for you.
              </Typography>

              {/* Method options */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  { icon: <SelfImprovementIcon sx={{ fontSize: 24, color: '#8B5CF6' }} />, label: 'Decode Your Stress — Let Echo Sounds Reset You', subtitle: 'Body scan + energy check with personalised sound therapy', bg: '#F3F0FF', disabled: false, mode: 'decode' },
                  // { icon: <CameraFrontIcon sx={{ fontSize: 24, color: '#8B5CF6' }} />, label: 'Face Recognition', subtitle: 'Analyze your expressions via camera', bg: '#F3F0FF', disabled: false, mode: 'face' },
                  { icon: <WatchIcon sx={{ fontSize: 24, color: '#EC4899' }} />, label: 'Biometric', subtitle: 'Use sensors to measure your vitals', bg: '#FDF2F8', disabled: false, mode: 'biometric' },
                  { icon: <AssignmentIcon sx={{ fontSize: 22, color: '#0d9488' }} />, label: 'Manual', subtitle: 'Self-report your current state', iconBg: 'rgba(13,148,136,.1)', disabled: false, mode: 'manual' },
                ].map((method, idx) => (
                  <Card
                    key={idx}
                    onClick={() => {
                      if (method.disabled) return;
                      setOpenMethodDialog(false);
                      if (method.mode === 'decode') {
                        navigate("/decode-stress");
                      } else {
                        setBiometricMode(method.mode || 'manual');
                        setOpenBiometricDialog(true);
                      }
                    }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2, p: 2,
                      borderRadius: 3,
                      border: '1px solid #E5E7EB',
                      opacity: method.disabled ? 0.5 : 1,
                      transition: 'all 0.2s',
                      cursor: method.disabled ? 'not-allowed' : 'pointer',
                      '&:hover': method.disabled ? {} : {
                        borderColor: '#22C55E',
                        boxShadow: '0 2px 12px rgba(34,197,94,0.15)',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <Box sx={{
                      width: 44, height: 44, borderRadius: '12px',
                      background: method.bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {method.icon}
                    </Box>
                    <Box sx={{ textAlign: 'left', flex: 1 }}>
                      <Typography fontWeight={600} fontSize={14}>
                        {method.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontSize={12}>
                        {method.subtitle}
                      </Typography>
                    </Box>
                    {method.disabled && (
                      <Chip
                        label="Coming Soon"
                        size="small"
                        sx={{
                          fontSize: 10, fontWeight: 600, height: 22,
                          background: 'linear-gradient(135deg, #F3F0FF, #FDF2F8)',
                          color: '#8B5CF6', border: '1px solid #E9D5FF',
                        }}
                      />
                    )}
                  </Card>
                ))}
              </Box>
            </Box>
          </Dialog>
          <PageContainer>
            {/* Ambient Background */}
            <AmbientBackground>
              <Orb top="-200px" right="-140px" size="520px" color="rgba(26,174,122,0.38)" />
              <Orb bottom="-100px" left="-80px" size="380px" color="rgba(139,92,246,0.22)" delay="0s" />
              <Orb top="45%" left="35%" size="300px" color="rgba(245,158,11,0.18)" delay="4s" />
              <Orb top="20%" right="20%" size="200px" color="rgba(13,148,136,0.15)" delay="8s" />
            </AmbientBackground>

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 40px',
            }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: '#22c55e',
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}
              >
                echo Sound Therapy
              </Typography>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  background: '#F0F5FF',
                  width: { xs: 32, sm: 36, md: 42 },
                  height: { xs: 32, sm: 36, md: 42 },
                  borderRadius: '50%',
                  color: '#0B2D6B',
                  boxShadow: '0 6px 16px rgba(26,174,122,0.3)',
                  transition: 'all 0.3s ease',

                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 22px rgba(26,174,122,0.4)',
                    background: '#F0F5FF',
                  },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
              </IconButton>
            </Box>
            <ContentWrapper>
              <HeroSection>
                <HeroParticles>
                  {heroParticles.map((particle, i) => (
                    <span
                      key={i}
                      style={{
                        left: particle.left,
                        bottom: '-10px',
                        animationDuration: particle.duration,
                        animationDelay: particle.delay,
                        width: particle.size,
                        height: particle.size,
                      }}
                    />
                  ))}
                </HeroParticles>

                <HeroLeft>

                  <HeroTitle>Heal with Sound Therapy</HeroTitle>
                  <HeroSubtitle>
                    Discover healing through personalized frequencies, immersive soundscapes, and guided meditations — all tuned to your well-being.
                  </HeroSubtitle>
                  <WaveIndicator onClick={() => setOpenMethodDialog(true)} style={{ cursor: 'pointer' }}>
                    {waveBars.map((bar, i) => (
                      <div key={i} className="wave-bar" style={{ animationDelay: bar.delay, height: '5px' }} />
                    ))}
                    <span className="wave-label">Start Your Session Now</span>
                  </WaveIndicator>
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1.5,
                    width: 'fit-content',
                  }}>
                    <HeadphonesIcon sx={{ fontSize: 17, color: '#fff', flexShrink: 0 }} />
                    <Typography sx={{
                      fontSize: 12.5,
                      color: 'rgba(255,255,255,0.9)',
                      fontWeight: 500,
                      letterSpacing: '0.01em',
                      lineHeight: 1.4,
                    }}>
                      For the best experience, put your headphones on and allow yourself to fully unwind
                    </Typography>
                  </Box>
                </HeroLeft>

                <HeroRight>
                  <FeaturedCard>
                    <FeaturedInner>
                      <FeaturedImage>
                        <FeaturedChip sx={{ display: { xs: 'none', sm: 'block' } }}>Featured</FeaturedChip>
                        <img src={ManageYourStressImg} alt="Manage Anxiety" loading="lazy" />
                      </FeaturedImage>
                      <FeaturedBody>
                        <CardTitle>
                          Manage Your<br />Anxiety & Stress
                        </CardTitle>
                        <CardButton>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4" />
                            <path d="M12 8h.01" />
                          </svg>
                          Personalised experience based on your Stress levels
                        </CardButton>
                      </FeaturedBody>
                    </FeaturedInner>
                  </FeaturedCard>
                </HeroRight>
              </HeroSection>

              <SectionHead>
                <SectionIcon color="green">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18V5l12-2v13" />
                    <circle cx="6" cy="18" r="3" />
                    <circle cx="18" cy="16" r="3" />
                  </svg>
                </SectionIcon>
                <SectionTitle>Explore Therapies for Quick Reset </SectionTitle>
                <SectionLine />
                {/* <SectionCount>4 Experiences</SectionCount> */}
              </SectionHead>

              {/* Assessment CTA Banner */}
              <Box
                onClick={() => setOpenSmartDialog(true)}
                sx={{
                  mb: 3, mt: 1,
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, #22c55e 0%, #14b8a6 55%, #8b5cf6 100%)',
                  boxShadow: '0 10px 36px rgba(20,184,166,0.22)',
                  p: { xs: '20px 22px', md: '24px 36px' },
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 2, flexWrap: 'wrap', cursor: 'pointer', overflow: 'hidden',
                  position: 'relative',
                  transition: 'transform 0.25s, box-shadow 0.25s',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 18px 48px rgba(20,184,166,0.32)' },
                  '&::before': {
                    content: '""', position: 'absolute', top: -60, right: -60,
                    width: 200, height: 200, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.06)', pointerEvents: 'none',
                  },
                }}
              >
                {/* Left: icon + text */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 220 }}>
                  <Box sx={{
                    width: 52, height: 52, borderRadius: '14px', flexShrink: 0,
                    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                    </svg>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', md: '1.1rem' }, color: '#fff', lineHeight: 1.3, mb: 0.4 }}>
                      Not sure which sound therapy you need today?
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                      Take a quick wellness check-in — we'll recommend the perfect session for you.
                    </Typography>
                  </Box>
                </Box>

                {/* Right: CTA */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8, flexShrink: 0 }}>
                  <GlowButton onClick={(e) => { e.stopPropagation(); setOpenSmartDialog(true); }}>
                    <ButtonGlow />
                    <span style={{ position: 'relative', zIndex: 1 }}>Take Assessment</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'relative', zIndex: 1 }}>
                      <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
                    </svg>
                  </GlowButton>
                  <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.45)', letterSpacing: '0.3px' }}>
                    5 min · Free · 12 questions
                  </Typography>
                </Box>
              </Box>

              <CardsGrid>
                {categories.map((category, index) => (

                  <TherapyCard key={index} onClick={() => {
                    const user = JSON.parse(localStorage.getItem('user') || '{}');
                    trackCTA('sound_therapy', user);
                    navigate(categoryRoutes[category.name] || '/care');
                  }} style={{ animationDelay: `${index * 0.07}s` }}>
                    <CardInner>
                      <CardImage>
                        <img src={category.image_url} alt={category.display_name} loading="lazy" />
                      </CardImage>
                      <CardBody
                        gradient={categoryGradients[category.name] || "linear-gradient(135deg, #0FA89E, #3DD9C8)"}>
                        <CardTitle>{category.display_name}</CardTitle>
                        <CardButton>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                            <path d="M9 18V5l12-2v13" />
                            <circle cx="6" cy="18" r="3" />
                            <circle cx="18" cy="16" r="3" />
                          </svg>
                          {category.title}
                        </CardButton>
                      </CardBody>
                    </CardInner>
                  </TherapyCard>
                ))}
              </CardsGrid>

              <div id="feedback">
                <SectionHead style={{ marginTop: 40 }}>
                  <SectionIcon color="teal">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </SectionIcon>
                  <SectionTitle>Record Your Feedback</SectionTitle>
                  <SectionLine />
                </SectionHead>
              </div>
              <FeedbackCard>
                <FeedbackPrompt>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1AAE7A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <span>How was your session? Share your experience to help us personalise your journey.</span>
                </FeedbackPrompt>

                <FeedbackMoodRow>
                  <FeedbackMoodLabel>Rate your experience</FeedbackMoodLabel>
                  <MoodButtons>
                    {moods.map((mood, index) => (
                      <MoodButton
                        key={index}
                        selected={selectedMood === index}
                        onClick={() => setSelectedMood(index)}
                      >
                        {mood}
                      </MoodButton>
                    ))}
                  </MoodButtons>
                </FeedbackMoodRow>

                <FeedbackInput
                  placeholder="Tell us what you felt during the session..."
                  rows={3}
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                />

                <FeedbackActions>
                  <FeedbackTags>
                    {tags.map((tag) => (
                      <TagButton
                        key={tag}
                        selected={selectedTags.includes(tag)}
                        onClick={() => handleTagToggle(tag)}
                      >
                        {tag}
                      </TagButton>
                    ))}
                  </FeedbackTags>
                  <SubmitButton
                    onClick={handleSubmitFeedback}
                    disabled={!isFormValid}
                  >
                    Submit Feedback
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14" />
                      <path d="M12 5l7 7-7 7" />
                    </svg>
                  </SubmitButton>
                </FeedbackActions>
              </FeedbackCard>

              <SectionHead style={{ marginTop: 40 }}>
                <SectionIcon color="green">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </SectionIcon>
                <SectionTitle>What People Say</SectionTitle>
                <SectionLine />
              </SectionHead>

              <Slider {...settings}>
                {feedbacks.map((item) => (
                  <div key={item.id}>
                    <TestimonialCard>
                      <TestimonialAvatar>🎵</TestimonialAvatar>

                      <TestimonialBody>
                        <TestimonialQuote>
                          "{item.comment}"
                        </TestimonialQuote>

                        <TestimonialMeta>
                          <TestimonialName>{item.user_name}</TestimonialName>
                          <TestimonialMetaDot />
                          <TestimonialDetail>
                            Emotions: {item.current_emmotion}
                          </TestimonialDetail>

                          <TestimonialStars>
                            {[...Array(5)].map((_, i) => (
                              <svg
                                key={i}
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill={i < item.experience ? "currentColor" : "#ddd"}
                              >
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                              </svg>
                            ))}
                          </TestimonialStars>
                        </TestimonialMeta>
                      </TestimonialBody>
                    </TestimonialCard>
                  </div>
                ))}
              </Slider>
            </ContentWrapper>
          </PageContainer>
        </>
      )}
    </>
  );
};

export default EchoSounds;



